<script setup lang="ts">
import Bowser from 'bowser'
import { onKeyStroke, useThrottleFn, useWebSocket } from '@vueuse/core'
import { useRouteQuery } from '@vueuse/router'
import { useNProgress } from '@vueuse/integrations/useNProgress'
import { DEFAULT_INTERVAL_TIME, DEFAULT_VIDEO_QUALITY, VIDEO_QUALITY } from '@/contants/game'

import { API_GET_WS_LINK, API_RECORD_ERROR, API_VISITOR_AUTH } from '@/api'

import SVG_LOGO_PRINT from '@/assets/svg/print.svg'
import { EResponseCode, ESendType } from '@/types/websocket'
import { EGamePlayGroundAlertEvent, EGamePlayGroundAlertType, EGameType, ELoginSuccessSource, EToastType } from '@/types/components'
import { EControlSystemKey } from '@/utils/cloud-device-sdk'

import type { IGamePlayGroundProps } from '@/types/components'
import type { IDeviceInfo, IWsResponseData } from '@/types/websocket'

import { GameControlBar, RtcVideo } from '#components'

const props = defineProps<{
  detail: IGamePlayGroundProps
}>()

const timeline: Record<string, string | number> = {}
const openid = useRouteQuery('utm_id')

const { isMobile, isDesktop } = useDevice()

const isDisplayFullscreenPanelAds = ref(false)
let isHookServerDowntimeAutoReconnect = false
let startConnectDate = 0
let waitConnectTimeCount = 0
let deviceWsIsActive = false
const isPlay = ref(false)
const visibleListener = ref()
const refreshCloudElementKey = ref(0)
const cloudGameElement = ref<InstanceType<typeof RtcVideo>>()
const controlBarElement = ref<InstanceType<typeof GameControlBar>>()
const webGameElement = ref<HTMLIFrameElement>()
const deviceId = ref<number>(0)
const isShowAlert = ref<EGamePlayGroundAlertType>(EGamePlayGroundAlertType.CLOSE_ALERT)
const wsUrl = ref<undefined | string>(undefined)
const cloudPhoneConnectPayload = reactive({
  phoneId: '',
  publicIp: '',
  accessPort: 0,
})
const cloudGameAuth = reactive({
  token: '',
  uid: '',
})
const queueRank = ref(0)
const cloudGameIsMuted = useLocalStorage<boolean>('EASYFUN_IS_MUTED', false, { serializer: { read: JSON.parse, write: JSON.stringify } })
const cloudGameQuality = useLocalStorage<number>('EASYFUN_VIDEO_QUALITY', DEFAULT_VIDEO_QUALITY, { serializer: { read: Number, write: JSON.stringify } })

// game countdown
const userRemainingTime = ref(0)
const addCountdownTime = ref(0)

const cloudGamePing = ref(0)
const ipInfo = ref<string | null>('')
const isReconnect = ref<boolean>(false)

const { globalContext } = useGlobalContext()
const { t } = useI18n()
const route = useRoute()
const { toast } = useToast()
const { userInfo, isLogged, cleanUserInfo, loginSource } = useUserInfo()
const { addPlayedGame } = usePlayed()
const { setIsShowPersonalPopup } = usePersonalPopup()

const screenSize = ref({ width: `0px`, height: `0px` })
const iosElementFullscreen = ref<boolean>(false)
const fullscreenElement = ref<HTMLElement | null>(null)
const { gameButtonMapPosition } = useCloudGameKeyBoardSet(props.detail)
const { isFullscreen, enter: enterBrowserFullscreen, exit: exitBrowserFullscreen, isSupported: isSupportedFullscreen } = useFullscreen(fullscreenElement)
const fullscreenLocked = useScrollLock(fullscreenElement)
const { updateWatermark } = useWatermark()

const { lockOrientation, unlockOrientation } = useScreenOrientation()
const { isLoading: palyBtnLoading, progress, start, done } = useNProgress(null, { minimum: 0.1, trickleSpeed: 150 })
const { isInDowntime, updateDowntimeStatus, waitDowntimeSeconds } = useServerDowntime()
const needShowBandPrintInGameStage = ref(false)
const isInScreenFollowMouseMode = ref(false)

// const { isNeedSeeAds, startLoadAds } = useFullscreenAds(props.detail.gameId)
const { isNeedSeeAds, updateIntervalTime } = useGameAdsState(props.detail.gameId)
const { initAds, startLoadAds, loadingAds } = useRewardAds(() => {
  updateIntervalTime()
  // enterFullscreen()
}, () => {
  updateIntervalTime(60 * 1000)
  enterFullscreen()
})

const { open: wsConnect, send: wsSend, close: wsClose, status: wsStatus } = useWebSocket(wsUrl, {
  heartbeat: {
    interval: DEFAULT_INTERVAL_TIME,
    message: JSON.stringify({ type: ESendType.PING }),
  },
  async onDisconnected(ws, event) {
    /* eslint-disable-next-line no-console */
    console.info(`%c ws disconnected: ${new Date().toLocaleString()}\nclose code: ${event.code}\n
    reason: ${event.reason}\n
    was clean: ${event.wasClean}\n
    uid: ${cloudGameAuth.uid}\n
    game id: ${props.detail.gameId}\n
    device id: ${deviceId.value}\n
    url: ${ws.url}
    `, 'background: #222; color: #ccff00; padding: 10px 0')
    const { needDisconnectAll, isInDowntimeNow } = await updateDowntimeStatus(true)
    if (isInDowntimeNow)
      needDisconnectAll && restGameStatus()
    else
      restGameStatus()
  },
  async onError(ws) {
    /* eslint-disable-next-line no-console */
    console.info(`%c ws error ${new Date().toLocaleString()}: \n device id: ${deviceId.value}\n ws url: ${ws.url}`, 'background: #222; color: #ccff00; padding: 10px 0')
    const { needDisconnectAll, isInDowntimeNow } = await updateDowntimeStatus(true)
    if (isInDowntimeNow)
      needDisconnectAll && restGameStatus()
    else
      restGameStatus()

    try {
      window.__bl.error(new Error(`Business WSS Error: id:[${deviceId.value}], address:${ws.url}, uid: ${cloudGameAuth.uid}`))
    }
    catch (err) {}
  },
  onMessage(_, event) {
    const res = JSON.parse(event.data)
    if (res) {
      if (res.code !== EResponseCode.PONG)
        handleWsRes(res)
    }
  },
})
const { getFingerprint } = useVisitor()

const alertContent = computed(() => {
  let varList = {}
  if (isShowAlert.value === EGamePlayGroundAlertType.QUEUE_INFO) {
    const signup = !isLogged.value
      ? `\n${t(`gameDetail.alert4SignContent`)}`
      : ''
    varList = { rank: queueRank.value, signup }
  }
  else if (isShowAlert.value === EGamePlayGroundAlertType.SERVER_IN_DOWNTIME) {
    varList = { min: waitDowntimeSeconds.value }
  }
  else if (isShowAlert.value === EGamePlayGroundAlertType.FREE_GAME_TIME_EXPIRED) {
    const content = isLogged.value ? t(`gameDetail.alert${isShowAlert.value}Content`) : t(`gameDetail.alert${isShowAlert.value}VisitorContent`)
    return content.replace(/\\n/g, '\n')
  }
  return t(`gameDetail.alert${isShowAlert.value}Content`, varList).replace(/\\n/g, '\n') || 'The site is currently under maintenance. \nWe should be back in a few mins :)'
})

const needInitAdsInFullscreenOnPC = computed(() => isDisplayFullscreenPanelAds.value && isFullscreen.value && isDesktop && props.detail.gameType !== EGameType.WEBSITE)

const { pause: removeListenWaitTime, resume: listenWaitTime } = useIntervalFn(() => {
  // collect connect wait time by seconds
  waitConnectTimeCount += 1
  try {
    window?.dataLayer?.push({ event: 'connect-wait-time', seconds: waitConnectTimeCount * 10, gameName: props.detail.gameName, uid: userInfo.value.uid || cloudGameAuth.uid, isLogged: Number(isLogged.value) })
  }
  catch (e) { }

  // 180s alert tips connect error
  if (waitConnectTimeCount >= 18) {
    restGameStatus()
    isShowAlert.value = EGamePlayGroundAlertType.REMINDER_CHANGE_BROWSER
  }
}, 10 * 1000)

const tapPlay = useThrottleFn(async () => {
  // ads block
  if (!window.EASYFUN_ADS_CAN_RUN && process.env.NODE_ENV !== 'development')
    return window.open(`https://docs.google.com/document/d/1I0XvarBMceDu_g9p-9whjW67E2cOpoinVq76RYEhkXw/edit`)

  if (props.detail.gameType === EGameType.CLOUD) {
    if (palyBtnLoading.value)
      return
    // check server in downtime
    const { isInDowntimeNow } = await updateDowntimeStatus()
    if (isInDowntimeNow) {
      isShowAlert.value = EGamePlayGroundAlertType.SERVER_IN_DOWNTIME
      return
    }

    timeline['-2'] = +new Date()
    try {
      // start loading game
      start()

      const fingerprintPromise = getFingerprint()
      // get user auth
      if (!isLogged.value) {
        const fingerprint = await fingerprintPromise
        const visitorInfo = await API_VISITOR_AUTH(fingerprint)
        cloudGameAuth.token = visitorInfo.token
        cloudGameAuth.uid = visitorInfo.uid
        try {
          window.__bl.setConfig({
            uid: cloudGameAuth.uid,
            setUsername: () => '#_visitor_#',
          })
        }
        catch (e) { }
      }
      else {
        cloudGameAuth.token = userInfo.value.shortToken
        cloudGameAuth.uid = userInfo.value.uid
      }

      // connect
      const fingerprint = await fingerprintPromise
      const responseWsInfo = await API_GET_WS_LINK({
        uid: cloudGameAuth.uid,
        token: cloudGameAuth.token,
        gameId: String(props.detail.gameId),
        fingerprint,
        email: userInfo.value.email || '',
        nickName: userInfo.value.nickname,
        platform: (openid.value as string) || '',
      })

      if (responseWsInfo.code === 200 && responseWsInfo.data) {
        wsUrl.value = responseWsInfo.data
        wsConnect()
        listenWaitTime()
      }
      else {
        if (responseWsInfo.code === EResponseCode.NO_MORE_USAGE_TIME)
          isShowAlert.value = EGamePlayGroundAlertType.TOTAL_GAME_TIME_EXPIRED
        else
          toast({ type: EToastType.ERROR, text: t('common.toastOops', 'Oops! Something went wrong.') })

        isPlay.value = false
        palyBtnLoading.value = false
      }
    }
    catch (err) {
      palyBtnLoading.value = false
    }
  }
  else {
    isPlay.value = true
    addPlayedGame(props.detail)
    document.querySelector<HTMLDivElement>('#game-stage')?.focus()
  }

  try {
    window?.dataLayer?.push({ event: props.detail.gameType === EGameType.CLOUD ? 'play-cloud-game' : 'play-web-game', gameName: props.detail.gameName, uid: cloudGameAuth.uid, isLogged: Number(isLogged.value) })
  }
  catch (e) { }
}, 1500)

const handleQueue = useThrottleFn((rank: number) => {
  queueRank.value = rank
  const timer = setTimeout(() => {
    wsSend(JSON.stringify({ type: ESendType.QUEUE }))
    clearTimeout(timer)
  }, 5000)
}, 2000)

function enterFullscreen() {
  if (isSupportedFullscreen.value) {
    // catch Permissions check failed error
    try {
      enterBrowserFullscreen()
    }
    catch (error) {
      // eslint-disable-next-line no-console
      console.info(error)
    }
  }
  else {
    isFullscreen.value = true
    iosElementFullscreen.value = true
    window.dispatchEvent(new Event('resize'))
  }
}

function exitFullscreen() {
  exitBrowserFullscreen()
  isFullscreen.value = false
  iosElementFullscreen.value = false
  window.dispatchEvent(new Event('resize'))
}

async function tapFullscreen() {
  if (isFullscreen.value || iosElementFullscreen.value) {
    exitFullscreen()
  }
  else {
    if (isNeedSeeAds.value)
      startLoadAds()
    else
      enterFullscreen()
  }

  try {
    window?.dataLayer?.push({ event: 'tap-fullscreen', gameName: props.detail.gameName, uid: userInfo.value.uid || cloudGameAuth.uid, isLogged: Number(isLogged.value) })
  }
  catch (e) { }
}

whenever(() => queueRank.value > 0, () => globalContext.value.gameState = 'idle')
watch(isPlay, v => globalContext.value.gameState = v ? 'playing' : 'queue')
watch(isFullscreen, (v) => {
  globalContext.value.isFullscreen = v
  v ? lockOrientation('portrait-primary') : unlockOrientation()
})
watch(iosElementFullscreen, (v) => {
  v && window.scrollTo({ top: 0 })
  fullscreenLocked.value = v
  globalContext.value.iosFullScreen = v
})

function tapReturnBtn() {
  cloudGameElement.value?.triggerSystemKey(EControlSystemKey.BACK)
}

function handleWsRes(response: IWsResponseData) {
  switch (response.code) {
    case EResponseCode.DEVICE_INFO: {
      // clear alert -> queue scenes
      if (isShowAlert.value === EGamePlayGroundAlertType.QUEUE_INFO)
        palyBtnLoading.value = true

      isShowAlert.value = EGamePlayGroundAlertType.CLOSE_ALERT

      const data = response.data as IDeviceInfo

      if (data.connectStatus === 2 && isHookServerDowntimeAutoReconnect) {
        isHookServerDowntimeAutoReconnect = false
        return
      }

      timeline['-1'] = +new Date()

      // connection to cloud device
      deviceId.value = data.deviceId
      cloudPhoneConnectPayload.phoneId = data.phoneId
      cloudPhoneConnectPayload.accessPort = data.accessPort
      cloudPhoneConnectPayload.publicIp = data.publicIp

      // invoke cloud game element
      refreshCloudElementKey.value += 1
      ipInfo.value = data.ipInfo
      isReconnect.value = data.connectStatus === 2

      // time cut
      userRemainingTime.value = data.remainingTime
      break
    }
    case EResponseCode.DUPLICATE_CONNECTION: {
      isShowAlert.value = EGamePlayGroundAlertType.DUPLICATE_CONNECTION
      restGameStatus()
      break
    }
    case EResponseCode.QUEUE_INFO:
    case EResponseCode.NO_MORE_DEVICE: {
      const { queueValue: rank } = response.data as { queueValue: number, queueSize: number }
      if (rank < 1)
        isShowAlert.value = EGamePlayGroundAlertType.CLOSE_ALERT
      else
        handleQueue(rank)

      if (response.code === EResponseCode.NO_MORE_DEVICE)
        isShowAlert.value = EGamePlayGroundAlertType.QUEUE_INFO

      palyBtnLoading.value = false
      break
    }
    case EResponseCode.DEVICE_EXPIRES:
      isShowAlert.value = EGamePlayGroundAlertType.FREE_GAME_TIME_EXPIRED
      break
    case EResponseCode.NO_MORE_USAGE_TIME:
      isShowAlert.value = EGamePlayGroundAlertType.TOTAL_GAME_TIME_EXPIRED
      break
    case EResponseCode.ADD_GAME_TIME:
      addCountdownTime.value = response.data as number
      break
    default:
      response.code === EResponseCode.USER_TOKEN_ERROR && cleanUserInfo()
      toast({ type: EToastType.ERROR, text: t(`gameDetail.wsErrorTips${response.code}`, t('common.toastOops', 'Oops! Something went wrong.')) })
      break
  }
}

function tapAlertBtn(event: EGamePlayGroundAlertEvent) {
  if (isShowAlert.value === EGamePlayGroundAlertType.LONG_TIME_NO_ACTIVE && event === EGamePlayGroundAlertEvent.CONFIRM) {
    // reconnect
    tapPlay()
  }
  else if (isShowAlert.value === EGamePlayGroundAlertType.QUEUE_INFO) {
    // quit queue
    wsSend(JSON.stringify({ type: ESendType.CANCEL }))
    restGameStatus()
  }
  else if (isShowAlert.value === EGamePlayGroundAlertType.REMINDER_CHANGE_BROWSER) {
    window.open(import.meta.env.VITE_DISCORD_URL)
  }
  // close window
  isShowAlert.value = EGamePlayGroundAlertType.CLOSE_ALERT
}

function handleAlertContentTap(e: { target: HTMLElement }) {
  const clickEvent = e.target.getAttribute('click-target-event')
  if (clickEvent === EGamePlayGroundAlertEvent.SIGNUP) {
    restGameStatus()
    setIsShowPersonalPopup(true)
    loginSource.value = EGamePlayGroundAlertType.QUEUE_INFO ? ELoginSuccessSource.QUEUE_POPUP_BTN : ELoginSuccessSource.TIME_EXPIRED_POPUP
    isShowAlert.value = EGamePlayGroundAlertType.CLOSE_ALERT
  }
}

// track99Error
const { start: track99Error, stop: stopTrack } = useTimeoutFn(() => {
  if (palyBtnLoading.value && !isPlay.value && !import.meta.env.DEV) {
    API_RECORD_ERROR(JSON.stringify({
      browserLang: navigator.language || 'unknown',
      uid: cloudGameAuth.uid,
      nickname: userInfo.value.nickname || 'visitor',
      deviceId: deviceId.value,
      ipInfo: ipInfo.value,
      localDate: new Date().toLocaleString(),
      gameName: props.detail.gameName,
      gameId: props.detail.gameId,
    }))

    stopTrack()

    try {
      window?.dataLayer?.push({ event: 'track-99-error', uid: cloudGameAuth.uid, gameName: props.detail.gameName, isLogged: Number(isLogged.value) })
    }
    catch (e) { }
  }
}, 20000, { immediate: false })

function gameWindowDisplay() {
  isPlay.value = true
  palyBtnLoading.value = false
  idleDisconnect()
  done()
  document.querySelector<HTMLDivElement>('#game-stage')?.focus()
  // when in mobile tap play auto tap fullscreen
  isMobile && nextTick(tapFullscreen)
  nextTick(() => {
    window.dispatchEvent(new Event('resize'))
  })
  try {
    const { countryCode, timeZone, city } = JSON.parse(ipInfo.value || '{}') as Record<string, string>
    const timeConsumed = Math.floor((performance.now() - startConnectDate) / 1000)
    const timer = setTimeout(() => {
      // wait ping data
      window?.dataLayer?.push({ event: 'game-started', ping: cloudGamePing.value, countryCode, timeZone, city, gameName: props.detail.gameName, timeConsumed, uid: userInfo.value.uid || cloudGameAuth.uid, isLogged: Number(isLogged.value) })
      clearTimeout(timer)
    }, 5000)
  }
  catch (e) { }
}

function handelShowCloudGameElement() {
  // game started
  deviceWsIsActive = true
  addPlayedGame(props.detail)
  stopTrack()
  gameWindowDisplay()
}

function couldGameDisconnect() {
  try {
    if (props.detail.gameType === EGameType.CLOUD) {
      cloudGameElement.value?.reset()
      wsStatus.value === 'OPEN' && wsClose(4001, `Client actively disconnects`)
    }
  }
  catch (e) { }
}

function handleQueueReconnect() {
  toast({ type: EToastType.ERROR, text: t('common.toastOops') })
  isShowAlert.value = EGamePlayGroundAlertType.CLOSE_ALERT
  wsConnect()
}

function restGameStatus() {
  isPlay.value = false
  palyBtnLoading.value = false
  waitConnectTimeCount = 0
  couldGameDisconnect()
  removeListenWaitTime()
  visibleListener.value?.off('idle')
  exitFullscreen()
  isFullscreen.value = false
  iosElementFullscreen.value = false
  isReconnect.value = false
  isHookServerDowntimeAutoReconnect = false
  if (isShowAlert.value === EGamePlayGroundAlertType.QUEUE && queueRank.value > 0)
    handleQueueReconnect()
}

async function idleDisconnect() {
  try {
    let isWakeUp = false
    const { ifvisible } = await import('@rosskevin/ifvisible')
    visibleListener.value = ifvisible
    visibleListener.value.setIdleDuration(300)
    visibleListener.value.idle(() => {
      isWakeUp = false
      isShowAlert.value = EGamePlayGroundAlertType.LONG_TIME_NO_ACTIVE
      const timer = setTimeout(() => {
        clearTimeout(timer)
        if (!isWakeUp) {
          wsSend(JSON.stringify({ type: ESendType.PAUSE }))
          restGameStatus()
        }
      }, 3000)
    })

    ifvisible.wakeup(() => {
      isWakeUp = true
    })
  }
  catch (err) { }
}

function toggleVolume() {
  cloudGameIsMuted.value = !cloudGameIsMuted.value
  try {
    window?.dataLayer?.push({ event: 'click-volume', muted: Number(cloudGameIsMuted.value), gameName: props.detail.gameName, uid: userInfo.value.uid || cloudGameAuth.uid, isLogged: Number(isLogged.value) })
  }
  catch (e) { }
}

function tapTipsBarLogin() {
  loginSource.value = ELoginSuccessSource.GAME_PANEL_TIPS_BAR_BTN
  setIsShowPersonalPopup(true)
}

function closeBusinessWs() {
  deviceWsIsActive = false;
  (isPlay.value || palyBtnLoading.value) && restGameStatus()
}

let missBusinessConnectCount = 0
async function handleUpdatePing(rtt: number) {
  cloudGamePing.value = rtt
  if (wsStatus.value !== 'OPEN') {
    if (missBusinessConnectCount > 10)
      restGameStatus()
    missBusinessConnectCount += 1
  }
  else {
    missBusinessConnectCount = 0
  }
}

watchOnce(() => (progress.value as number * 100).toFixed(0) === '99', track99Error)

watchEffect(() => {
  if (isShowAlert.value > EGamePlayGroundAlertType.CLOSE_ALERT) {
    try {
      window?.dataLayer?.push({ event: 'pop-up', type: isShowAlert.value, gameName: props.detail.gameName, uid: userInfo.value.uid || cloudGameAuth.uid, isLogged: Number(isLogged.value) })
    }
    catch (e) { }
  }
})

// listener connect time report
watch(() => palyBtnLoading.value, () => {
  if (palyBtnLoading.value) {
    startConnectDate = performance.now()
    listenWaitTime()
  }
  else {
    removeListenWaitTime()
    waitConnectTimeCount = 0
  }
}, { immediate: true })

// auto reconnect
watch(() => isInDowntime.value, () => {
  if (isInDowntime.value)
    return
  if (wsStatus.value === 'CLOSED' && deviceWsIsActive) {
    console.warn(`Hook auto reconnect business websocket`)
    isHookServerDowntimeAutoReconnect = true
    wsConnect()
  }
})

watch(deviceId, async () => {
  await nextTick()
  updateWatermark({ content: `${deviceId.value}`, parent: document.querySelector('#fullscreen-element')! })
})

// ads in fullscreen
watch(isFullscreen, () => {
  if (isFullscreen.value && isDesktop) {
    isDisplayFullscreenPanelAds.value = true
    const timer = setTimeout(() => {
      (window.adsbygoogle || []).push({});
      (window.adsbygoogle || []).push({})
      clearTimeout(timer)
      const timer2 = setTimeout(() => {
        // hide ads
        isDisplayFullscreenPanelAds.value = false
        nextTick(() => {
          window.dispatchEvent(new Event('resize'))
        })
        clearTimeout(timer2)
      }, 1000 * 10)
    }, 1000 * 3)
  }
})

onMounted(() => {
  const { start } = route.query
  start && tapPlay()

  needShowBandPrintInGameStage.value = window.innerHeight <= 725
  screenSize.value = { width: `${window.innerWidth}px`, height: `${window.innerHeight}px` }
  window.addEventListener('resize', () => {
    needShowBandPrintInGameStage.value = window.innerHeight <= 725
    screenSize.value = { width: `${window.innerWidth}px`, height: `${window.innerHeight}px` }
  })

  initAds()
})

onBeforeUnmount(() => {
  couldGameDisconnect()
})

useHead(() => ({
  htmlAttrs: {
    playing: isPlay.value,
  },
}))
</script>

<template>
  <section
    class="play-stage relative z-3 mx-auto w-full flex-1 flex-col items-end justify-end of-hidden rd-4 bg-#000"
    :class="{ 'aspect-ratio-880/496': !isPlay, 'mobile-box': isMobile }"
  >
    <!-- detail.banner -->
    <div
      v-if="!isPlay"
      class="absolute left-50% top-50% z-3 aspect-ratio-880/496 h-full max-h-100vh translate--50% bg-cover bg-center"
      :style="`background-image: url('${(detail.photos as any)?.image || detail.icon}'); filter: blur(${(detail.photos as any)?.image ? 0 : 50}px)`"
    />

    <!-- game info -->
    <div
      v-if="!isPlay" class="absolute inset-is-0 top-0 z-3 size-full flex-center flex-col"
      :style="`background-color: rgba(0, 0, 0, ${(detail.photos as any)?.image ? .7 : .5});`"
    >
      <img
        v-if="detail.icon" :src="`${detail.icon}?x-oss-process=image/resize,w_200`" class="size-25 rd-4 object-cover"
        :alt="detail.gameName"
      >
      <h1 class="mt-3 text-8 c-#fff font-normal font-[Bebas_Neue] md:mt-4">
        {{ detail.gameName || '' }}
      </h1>

      <!-- play btn group -->
      <GameStartBtn :is-cloud-game="detail.gameType !== EGameType.WEBSITE" :is-loading="palyBtnLoading" :progress="(progress as number)" @tap-play="tapPlay" />

      <!-- tips control -->
      <GameTips
        v-if="detail.gameType !== EGameType.WEBSITE"
        :pkg="detail.packageName"
        @tap-show-location-alert="isShowAlert = EGamePlayGroundAlertType.SERVER_LOCATION_TIPS"
        @tap-sign-in="tapTipsBarLogin"
      />
    </div>

    <ClientOnly>
      <div
        v-if="detail.gameType === EGameType.WEBSITE || (detail.gameType === EGameType.CLOUD && deviceId > 0)" id="fullscreen-element" ref="fullscreenElement"
        :key="`cloud-game-${refreshCloudElementKey}-block`" class="a relative size-full overflow-hidden"
        :class="iosElementFullscreen && 'fixed! left-0 top-0 z-999! fake-fullscreen'"
        :style="iosElementFullscreen && { ...screenSize }"
      >
        <div
          class="video-panel relative aspect-ratio-880/496 flex flex-wrap"
          :class="{ 'aspect-ratio-auto size-full': (isFullscreen || iosElementFullscreen) && !isDesktop }"
        >
          <ClientOnly v-if="needInitAdsInFullscreenOnPC">
            <div class="h-90px w-full flex-center">
              <ins
                class="adsbygoogle"
                style="display:inline-block;width:970px;height:90px"
                data-ad-client="ca-pub-3593861583707338"
                data-ad-slot="5325703312"
              />
            </div>
          </ClientOnly>
          <div class="w-full flex" :class="{ 'h-[calc(100%-90px)]': needInitAdsInFullscreenOnPC }">
            <div
              class="relative aspect-ratio-880/496 flex-center"
              :class="{ 'aspect-ratio-auto size-full': (isFullscreen || iosElementFullscreen) && !isDesktop, 'cursor-none': isInScreenFollowMouseMode, 'w-[calc(100%-160px)]': needInitAdsInFullscreenOnPC }"
            >
              <template v-if="detail.gameType !== EGameType.WEBSITE">
                <RtcVideo
                  ref="cloudGameElement"
                  :cloud-phone-connect-payload="cloudPhoneConnectPayload"
                  :device-id="deviceId"
                  :auth="cloudGameAuth"
                  :ip-info="ipInfo"
                  :game-info="{ gameId: detail.gameId, packageName: detail.packageName, minutes: 99999, screenOrientation: detail.screenOrientation }"
                  :is-reconnect="isReconnect"
                  :muted="cloudGameIsMuted"
                  :quality="Math.abs(cloudGameQuality - VIDEO_QUALITY.length) - 1"
                  :button-position="gameButtonMapPosition"
                  :is-full-screen="isFullscreen || iosElementFullscreen"
                  @update-rtt="handleUpdatePing"
                  @video-playing="handelShowCloudGameElement"
                  @close="closeBusinessWs"
                  @touchstart="controlBarElement?.setCtrlBarVisible(false)"
                />
                <SVG_LOGO_PRINT
                  v-if="needShowBandPrintInGameStage"
                  class="pointer-events-none absolute inset-ie-10 bottom-10 w-29 opacity-10"
                />
              </template>
              <template v-else>
                <!-- website game -->
                <iframe
                  v-if="detail.gameType === EGameType.WEBSITE" id="game-stage" ref="webGameElement"
                  class="aspect-ratio-880/496 w-full overflow-hidden" :class="isPlay ? '' : 'opacity-0'" :src="`${detail.playLink}?utm_source=distrib&utm_medium=easyfun.gg&skip-guard=1&clid=8417195`"
                  frameborder="0"
                />

                <div v-if="detail.gameType === EGameType.WEBSITE" class="mask-web-game absolute left-0 top-0 z-10 h-32px w-full opacity-0" :class="{ 'w-32px h-full!': isMobile }" />
              </template>
            </div>
            <ClientOnly v-if="needInitAdsInFullscreenOnPC">
              <div class="ads w-160px flex-center">
                <ins
                  class="adsbygoogle"
                  style="display:inline-block;width:160px;height:600px"
                  data-ad-client="ca-pub-3593861583707338"
                  data-ad-slot="6787621500"
                />
              </div>
            </ClientOnly>
          </div>
        </div>

        <!-- game ctrl bar -->
        <GameControlBar
          ref="controlBarElement"
          :detail="detail" :ping="cloudGamePing" :user-remaining-time="userRemainingTime"
          :is-fullscreen="isFullscreen || iosElementFullscreen" :is-mobile="isMobile"
          :is-need-see-ads="isNeedSeeAds" :loading-ads="loadingAds"
          :game-volume="cloudGameIsMuted" :current-selected-quality="cloudGameQuality"
          :add-countdown-time="addCountdownTime"
          :screen-width="fullscreenElement?.clientWidth"
          :screen-height="fullscreenElement?.clientHeight"
          @tap-fullscreen="tapFullscreen"
          @tap-return-btn="tapReturnBtn"
          @toggle-volume="toggleVolume"
          @switch-clarity="(quality) => cloudGameQuality = quality"
          @tap-exit="restGameStatus"
        />

        <!-- fullscreen print -->
        <SVG_LOGO_PRINT
          v-if="isFullscreen || iosElementFullscreen"
          class="pointer-events-none fixed inset-ie-5% bottom-5% w-50 opacity-10"
        />

        <Announcement
          v-if="isFullscreen || iosElementFullscreen"
          :is-fullscreen="isFullscreen || iosElementFullscreen"
        />
      </div>

      <!-- game catch alert -->
      <CommonAlert
        v-if="isShowAlert > EGamePlayGroundAlertType.CLOSE_ALERT"
        :title="t(`gameDetail.alert${isShowAlert}Title`) || 'Notification'" :content="alertContent"
        :btn-text="t(`gameDetail.alert${isShowAlert}Btn`) || 'Okay'" class="absolute z-12"
        @close="isShowAlert = EGamePlayGroundAlertType.CLOSE_ALERT" @confirm="tapAlertBtn"
        @handle-content-tap="handleAlertContentTap"
      />

      <!-- <GameAlertAds v-if="detail.gameType === EGameType.CLOUD" :action-show-alert="actionShowAlertAds" /> -->
    </ClientOnly>
  </section>
</template>

<style lang="scss" scoped>
// 1280/660
@media only screen and (min-aspect-ratio: 1280/600) and (max-height: 725px) {
  .play-stage {
    width: initial;
    height: calc(100vh - 40px);

    .video-panel {
      height: calc(100% - 88px);
    }
  }
}

.fade-in {
  opacity: 0;
  animation: fadeIn 300ms linear 600ms forwards;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
}

#game-stage {
  ::v-deep(.err-msg-tip),
  ::v-deep(.dragMove-wrap) {
    display: none;
  }
}

.fullscreen-show {
  display: none;

  &:fullscreen {
    background-color: DarkRed;
    color: white;
  }
}

@media (orientation: portrait) and (max-width: 1023.9px) {
  .mobile-box {
    width: 100vw !important;
    width: 100dvw !important;
    height: calc(100vh - 0.75rem - 3rem);
    height: calc(100dvh - 0.75rem - 3rem);
  }
}
</style>
